import React, { Component } from "react";
import { Row, Col, Label, FormGroup, Form } from "reactstrap";
import { AppBadge, AppCard, PageLayout, StatusBadge, createTabHeaderTemplate, LoadingDots } from "../../components/ui";
import { AdditionalDataEnum, UpdateUserPhotoRequest, UpdateUserProfileRequest, User, UserTypeRelation } from "../../models/User";
import { InputText } from "primereact/inputtext";
import { toastService } from "../../services/toastService";
import { userApiService } from "../../services/api/users/UsersApiService";
import { SelectItem } from "../../models/SelectItem";
import { utils } from "../../utils/utils";
import { ProfileImage } from "../../components/Image/ProfileImage";
import { fileService } from "../../services/FileService";
import { merchantGroupsApiService } from "../../services/api/merchants/MerchantGroupsApiService";
import { MerchantGroup } from "../../models/MerchantModel";
import _ from "lodash";
import { canSoftDeleteUser, isDataManager, isRestrictedToLeadsView, isSuperAdmin, isUserProfileUpdateAllowed } from "../../authGuard";
import { confirmAction } from "../../utils/confirmHelpers";
import { UserCog } from "lucide-react";
import * as Yup from "yup";
import { Image_File_Size, NAME_REGEX } from "../../models/AppConstants";
import imageCompression from "browser-image-compression";
import { ErrorMessage, Field, Formik } from "formik";
import { Button } from "primereact/button";
import { storageService } from "../../services/StorageService";
import DeleteUserDialog from "./DeleteUserDialog";
import { authUsersApiService } from "../../services/api/clients/AuthUsersApiService";

import history from "../../history";

type DeleteAction = "SOFT_DELETE" | "HARD_DELETE" | "RESTORE";
interface RegisteredUserProfileState {
    userId: number;
    activeTabIndex: number;
    registerUser: User | null;
    isLoading: boolean;
    groupsSelectItem: SelectItem<number>[];
    isDocumentLoading: boolean;
    isDeleteConfirmModalVisible: boolean,
    photoUploading: boolean;
    deleteAction: DeleteAction | null,
}

interface InitialValues {
    firstName: string;
    lastName: string;
    personalIdentificationNumber: string;
    email: string;
    phoneNumber: string;
}

export default class RegisteredUserProfile extends Component<Record<string, never>, RegisteredUserProfileState> {
    userStatusClassesMap: Map<string, string> = new Map<string, string>([
        ["Active", "success"],
        ["Deactivated", "danger"],
        ["Closed", "danger"],
        ["New", "success"],
        ["Pending Verification", "warning"],
        ["Blocked", "danger"],
    ]);
    reject = () => {
        // this.toast?.show({ severity: "warn", summary: "Rejected", detail: "You have rejected", life: 3000 });
    };
    resetEmailDialog = () => {
        confirmAction(
            "Are you sure to reset email address?",
            "Confirmation",
            () => {
                this.resetEmail(this.state.userId);
            },
        );
    };
    resetMobileDialog = () => {
        confirmAction(
            "Are you sure to reset mobile?",
            "Confirmation",
            () => {
                this.resetMobile(this.state.userId);
            },
        );
    };
    deactivateDialog = () => {
        confirmAction(
            "Are you sure to deactivate user status?",
            "Confirmation",
            () => {
                this.deactiveOrActiveUserAccount(this.state.userId);
            },
        );
    };

    validationSchema = Yup.object().shape({

        firstName: Yup.string()
            .required("First Name is required.")
            .max(75, "First Name must be less than 75 characters.")
            .matches(NAME_REGEX, "First name can only contain letters and single dashes, and must not start or end with a dash or space."),

        lastName: Yup.string()
            .required("Last Name is required.")
            .max(75, "Last Name must be less than 75 characters.")
            .matches(NAME_REGEX, "First name can only contain letters and single dashes, and must not start or end with a dash or space."),

        // personalIdentificationNumber: Yup.string()
        //     .required("Personal Identification Number is required.")
        //     .max(20, "Personal Identification Number must be less than 20 characters."),

        // email: Yup.string().email("Invalid email").required("Email is required"),
        // phoneNumber: Yup.string().required("Phone number is required"),
    });

    constructor(props: Record<string, never>) {
        super(props);

        this.state = {
            userId: utils.getIdentifierFromPath(),
            activeTabIndex: 0,
            registerUser: null,
            isLoading: false,
            groupsSelectItem: [],
            isDocumentLoading: false,
            isDeleteConfirmModalVisible: false,
            deleteAction: null,
            photoUploading: false,
        };
    }

    statusTypes: Map<number, string> = new Map<number, string>();

    async componentDidMount() {
        await this.loadUserData();
    }

    loadUserData = async () => {
        this.setState({ isLoading: true });
        const userProfile = await userApiService.getRegisteredUser(this.state.userId);
        if (!userProfile.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfile.message);
            return;
        }

        storageService.setSelectedMerchantId(userProfile.data.merchantId.toString());

        const statusTypes = await userApiService.getStatusTypes();

        if (!statusTypes.success) {
            this.setState({ isLoading: false });
            toastService.error(statusTypes.message);
            return;
        }

        statusTypes.data.forEach((item) => {
            this.statusTypes.set(item.value, item.label);
        });

        if (!isRestrictedToLeadsView() || isSuperAdmin()) {
            const groupResult = await merchantGroupsApiService.getAll(userProfile.data.merchantId); //
            if (!groupResult.success) {
                this.setState({ isLoading: false });
                toastService.error(groupResult.message);
                return;
            }
            this.setState({ groupsSelectItem: this.getSelectMerchantGroupItems(groupResult.data) });
        }

        this.setState({ registerUser: userProfile.data, isLoading: false });
    }

    loadImage = async () => {
        if (this.state.registerUser?.photo == null) {
            return;
        }
        const photoResult = await fileService.GetFile(this.state.registerUser?.photo);

        if (!photoResult.success) {
            toastService.error("Could not load User Photo. Please contact your administrator");
            return;
        }

        const user = this.state.registerUser;
        user.photo = photoResult.data;
        this.setState({ registerUser: user });
    };

    getSelectMerchantGroupItems = (options: MerchantGroup[]): SelectItem<number>[] => {
        return options.map(
            (data) =>
                ({
                    label: data.name,
                    value: data.id,
                }) as SelectItem<number>,
        );
    };

    getVerificationClassName = (param: boolean): string => {
        return param === true ? "success" : "danger";
    };

    getStatusVerificationClassName = (param: number): string => {
        const item = this.statusTypes.get(param);
        return item != null ? this.userStatusClassesMap.get(item)! : "danger";
    };


    updateValue = (fieldName: string, value: string) => {
        const user = this.state.registerUser as any;

        user[fieldName] = value;

        this.setState({ registerUser: user });
    };

    showToast = (message: string) => {
        toastService.success(message, "Action Completed");
    };

    deactiveOrActiveUserAccount = async (id: number) => {
        if (this.state.registerUser?.userStatus && this.statusTypes.get(this.state.registerUser.userStatus) === "Active") {
            this.deactivateUser(id);
            return;
        }

        this.activateUser(id);
    };

    deactivateUser = async (id: number) => {
        this.setState({ isLoading: true });
        const result = await userApiService.deactivateUser(id);

        if (!result.success) {
            this.setState({ isLoading: false });
            toastService.error(result.message);
            return;
        }

        // TODO. Fix it.

        const userProfile = await userApiService.get(this.state.userId);

        if (!userProfile.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfile.message);
            return;
        }

        this.setState({ registerUser: userProfile.data, isLoading: false });

        toastService.success("Account Deactivated.", "Action Completed");
    };

    closeUser = async (id: number) => {
        this.setState({ isLoading: true });
        const result = await userApiService.delete(id);

        if (!result.success) {
            this.setState({ isLoading: false });
            toastService.error(result.message);
            return;
        }

        // TODO. Fix it.

        const userProfile = await userApiService.get(this.state.userId);

        if (!userProfile.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfile.message);
            return;
        }

        this.setState({ registerUser: userProfile.data, isLoading: false });

        toastService.success("Account Closed.", "Action Completed");
    };

    activateUser = async (id: number) => {
        this.setState({ isLoading: true });
        const activeUser = await userApiService.activateUser(id);

        if (!activeUser.success) {
            this.setState({ isLoading: false });
            toastService.error(activeUser.message);
            return;
        }

        const userProfile = await userApiService.get(this.state.userId);

        if (!userProfile.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfile.message);
            return;
        }

        this.setState({ registerUser: userProfile.data, isLoading: false });

        toastService.success("Account Activated.", "Action Completed");
    };

    blockUserAccount = async (id: number) => {
        this.setState({ isLoading: true });

        const result = await userApiService.blockUser(id);

        this.setState({ isLoading: false });
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        //history.push("/users");
    };

    resetEmail = async (id: number) => {
        this.setState({ isLoading: true });

        const result = await userApiService.resetEmail(id);

        this.setState({ isLoading: false });
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        window.location.reload();
    };

    resetMobile = async (id: number) => {
        this.setState({ isLoading: true });

        const result = await userApiService.resetPhone(id);

        this.setState({ isLoading: false });
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        window.location.reload();
    };

    onImageSelected = async (image: File) => {
        if (!utils.isValidImage(image)) {
            toastService.error("Format not supported! Only Image files are allowed.", "File Format");
            return false;
        }

        if (image.size > Image_File_Size) {
            toastService.error("Files less than 5MB are supported only.", "File Size");
            return false;
        }

        this.setState({ photoUploading: true });
        const compressedImage = await imageCompression(image, { maxSizeMB: Image_File_Size, useWebWorker: true });
        const base64Image = await utils.convertFileToBase64(compressedImage);

        const request: UpdateUserPhotoRequest = {
            photo: base64Image as string,
            userId: this.state.userId,
            userType: UserTypeRelation.Individual,
        };

        const uploadResult = await userApiService.updateUserPhoto(request);

        if (!uploadResult.success) {
            this.setState({ photoUploading: false });
            toastService.error("Could not upload the image. Please try again.", "Upload Failed");
            return;
        }

        // Update user details with the uploaded image ID
        this.setState((prevState) => ({
            registerUser: {
                ...prevState.registerUser,
                photoStorageId: uploadResult.data,
                photo: base64Image as string,
            } as User,
            photoUploading: false,
        }));

        toastService.success("Profile image updated successfully.");
    };

    fileInput = (input: boolean) => {
        if (!input) {
            toastService.error("File Name Exceeds 128 Characters.", "File Name");
            return;
        }
    };
    getImageSource = (): string => {
        if (!this.state.registerUser || !this.state.registerUser.photo) {
            return ""
        }
        return this.state.registerUser.photo === null ? "" : utils.appendBase64Data(this.state.registerUser.photo);
    };

    onFieldUpdate = (fieldName: string, value: any) => {
        const userData = this.state.registerUser as any;
        userData[fieldName] = value;
        this.setState({ registerUser: userData });
    };

    handleSubmit = async (values: InitialValues) => {
        this.setState({ isLoading: true });
        const obj: UpdateUserProfileRequest = {
            userId: this.state.userId,
            additionalData: {
                type: AdditionalDataEnum.FastpayAdditionalData,
                data: {
                    firstName: values.firstName,
                    lastName: values.lastName
                }
            }
        }
        const usersResult = await userApiService.updateRegisteredUser(obj);
        this.setState({ isLoading: false });

        if (!usersResult.success) {
            toastService.error(usersResult.message);
            this.setState({ isLoading: false });
            return;
        }
        await this.loadUserData();
        toastService.success("User Updated Successfully");
    };

    showDeleteConfirmModal = (action: DeleteAction) => {
        this.setState({
            isDeleteConfirmModalVisible: true,
            deleteAction: action,
        });
    };

    hideDeleteModal = () => {
        this.setState({
            isDeleteConfirmModalVisible: false,
            deleteAction: null,
        });
    };
    renderUserDetails = () => {

        if (this.state.isLoading || this.state.registerUser == null) {
            return (
                <Row>
                    <Col md={12} lg={12}>
                        <AppCard className="min-h-[500px] align-center">
                            <LoadingDots />
                        </AppCard>
                    </Col>
                </Row>
            );
        }

        let firstName = "";
        let lastName = "";
        let personalIdentificationNumber = "";

        if (this.state.registerUser.additionalData && this.state.registerUser.additionalData.type === AdditionalDataEnum.FastpayAdditionalData) {
            firstName = this.state.registerUser.additionalData.data.firstName || "";
            lastName = this.state.registerUser.additionalData.data.lastName || "";
            personalIdentificationNumber = this.state.registerUser.additionalData.data.pin || "";
        }

        const initialValues = {
            firstName,
            lastName,
            personalIdentificationNumber,
            email: this.state.registerUser?.email || "",
            phoneNumber: this.state.registerUser?.phoneNumber || ""
        };
        return (
            <Row>
                <Col md="3" lg="3" sm="12" className="p-8">
                    {/* <ProfileImage
                        imageStyle={{ width: "100%" }}
                        className="rounded-circle"
                        src={this.getImageSource()}
                        alt=""
                        onImageSelected={this.onImageSelected}
                        fileInput={this.fileInput}
                        photoUploading={this.state.photoUploading}
                    /> */}
                </Col>

                <Formik initialValues={initialValues} validationSchema={this.validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        await this.handleSubmit(values);
                        setSubmitting(false);
                    }}>
                    {({ handleChange, handleBlur, values, handleSubmit }) => (
                        <Col md={9} lg={9} sm="12">
                            {(this.state.registerUser && this.state.registerUser.additionalData && this.state.registerUser.additionalData.type === AdditionalDataEnum.FastpayAdditionalData) && (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="firstName">First Name</Label>
                                                <InputText
                                                    name="firstName"
                                                    className="form-control"
                                                    value={values.firstName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                <div className="text-danger"><ErrorMessage name="firstName" /></div>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="lastName">Last Name</Label>
                                                <Field
                                                    name="lastName"
                                                    className="form-control"
                                                    value={values.lastName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                <div className="text-danger"><ErrorMessage name="lastName" /></div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="personalIdentificationNumber">Personal Identification Number</Label>
                                                <Field
                                                    name="personalIdentificationNumber"
                                                    className="form-control"
                                                    value={values.personalIdentificationNumber}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    readOnly
                                                    disabled
                                                />
                                                <div className="text-danger"><ErrorMessage name="personalIdentificationNumber" /></div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </>
                            )}
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="email">Email</Label>
                                        <Field
                                            name="email"
                                            type="email"
                                            className="form-control"
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            readOnly
                                            disabled
                                        />
                                        <div className="text-danger"><ErrorMessage name="email" /></div>
                                    </FormGroup>
                                </Col>
                                <Col md={3} className="flex items-center pt-4 px-2.5">
                                    <AppBadge variant={this.getVerificationClassName(this.state.registerUser!.isEmailVerified) as any}>
                                        {this.state.registerUser!.isEmailVerified ? "Verified" : "Not Verified"}
                                    </AppBadge>
                                </Col>
                                {/* {isUserProfileUpdateAllowed() && (
                                    <Col md={3} style={{ padding: "35px 10px" }}>
                                        <a href="#" onClick={this.resetEmailDialog}>
                                            Reset Email Address
                                        </a>
                                        <br></br>
                                        <a href="#" onClick={this.resetMobileDialog}>
                                            Reset Mobile
                                        </a>
                                        <br></br>

                                        {isUserProfileUpdateAllowed() && this.statusTypes.get(this.state.registerUser!.userStatus) === "Deactivated" && (
                                            <>
                                                <a href="#" onClick={() => this.deactiveOrActiveUserAccount(this.state.userId)}>
                                                    Activate
                                                </a>
                                                <span> | </span>
                                                <a style={{ padding: "0 2px" }} href="#" onClick={() => this.closeUser(this.state.userId)}>
                                                    Close
                                                </a>
                                                <span> | </span>
                                                <a style={{ padding: "0 2px" }} href="#" onClick={() => this.blockUserAccount(this.state.userId)}>
                                                    Add to Blacklist
                                                </a>
                                            </>
                                        )}

                                        {isUserProfileUpdateAllowed() && this.statusTypes.get(this.state.registerUser!.userStatus) === "Active" && (
                                            <>
                                                <a href="#" onClick={this.deactivateDialog}>
                                                    Deactivate
                                                </a>
                                            </>
                                        )}
                                    </Col>
                                )} */}
                            
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="phoneNumber">Phone</Label>
                                        <Field
                                            name="phoneNumber"
                                            className="form-control"
                                            value={values.phoneNumber}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            readOnly
                                            disabled
                                        />
                                        <div className="text-danger"><ErrorMessage name="phoneNumber" /></div>
                                    </FormGroup>
                                </Col>
                                <Col md={3} className="flex items-center pt-4 px-2.5">
                                    <AppBadge variant={this.getVerificationClassName(this.state.registerUser!.isPhoneNumberVerified) as any}>
                                        {this.state.registerUser!.isPhoneNumberVerified === true ? "Verified" : "Not Verified"}
                                    </AppBadge>
                                </Col>
                                {isUserProfileUpdateAllowed() && <Col md={3} className="pt-8 px-2.5"></Col>}
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label>
                                            <b>User Status</b>
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col className="pl-0" md={3}>
                                    <AppBadge variant={this.getStatusVerificationClassName(this.state.registerUser!.userStatus) as any}>
                                        {this.statusTypes.get(this.state.registerUser!.userStatus)}
                                    </AppBadge>
                                </Col>
                                {/* {isUserProfileUpdateAllowed() && this.statusTypes.get(this.state.registerUser!.userStatus) === "Deactivated" && (
                                    <Col>
                                        <a href="#" onClick={() => this.deactiveOrActiveUserAccount(this.state.userId)}>
                                            Activate
                                        </a>
                                        <span> | </span>
                                        <a style={{ padding: "0 2px" }} href="#" onClick={() => this.closeUser(this.state.userId)}>
                                            Close
                                        </a>
                                        <span> | </span>
                                        <a style={{ padding: "0 2px" }} href="#" onClick={() => this.blockUserAccount(this.state.userId)}>
                                            Add to Blacklist
                                        </a>
                                    </Col>
                                )} */}
                                {isUserProfileUpdateAllowed() && this.statusTypes.get(this.state.registerUser!.userStatus) === "Active" && <Col></Col>}
                            </Row>

                            <Row form>
                                <Col md={3}>
                                    <Label>
                                        <b>Merchant</b>
                                    </Label>
                                </Col>
                                <Col className="pl-0" md={3}>
                                    {this.state.registerUser!.merchant}&emsp;&emsp;<b className="text-danger">Branch - {this.state.registerUser!.flexCubeBranch}</b>
                                </Col>
                            </Row>
                            <Row form>
                                 <Col md={3}>
                                    {isDataManager() && !this.state.registerUser?.cbsId && (<>
                                    <a href="#" onClick={() => this.showDeleteConfirmModal("HARD_DELETE")}>
                                        Delete User Permanently
                                    </a>
                                </>
                                )}
                                </Col>
                                <Col md={3}>
                                {canSoftDeleteUser() && (
                                    <>
                                        {!utils.isSoftDeleted(values.email || "") ? (
                                            <a href="#" onClick={() => this.showDeleteConfirmModal("SOFT_DELETE")}>
                                                Archive User
                                            </a>
                                        ) : (
                                            <a href="#" onClick={() => this.showDeleteConfirmModal("RESTORE")}>
                                                Restore User
                                            </a>
                                        )}
                                    </>
                                )}
                                </Col>
                            </Row>

                            {(this.state.registerUser && this.state.registerUser.additionalData && this.state.registerUser.additionalData.type === AdditionalDataEnum.FastpayAdditionalData) && (
                                <div className="d-flex justify-content-end align-items-end">
                                    <div>
                                        <Button
                                            iconPos="right"
                                            type="button"
                                            label="Submit"
                                            onClick={() => handleSubmit()}
                                        // loading={this.state.btnLoader}
                                        // disabled={this.state.btnLoader}
                                        />
                                    </div>
                                </div>
                            )}
                        </Col>
                    )}
                </Formik>

            </Row>
        );
    };
    deleteConfirm = async (reason: string) => {
        if (!this.state?.userId || !this.state.deleteAction) return;

        try {
            let response;

            switch (this.state.deleteAction) {
                case "HARD_DELETE":
                    response = await authUsersApiService.hardDeleteUser({
                        userId: this.state.userId,
                        reason,
                    });
                    break;

                case "SOFT_DELETE":
                    response = await authUsersApiService.softDeleteUser({
                        userId: this.state.userId,
                        reason,
                    });
                    break;

                case "RESTORE":
                    response = await authUsersApiService.restoreUser({
                        userId: this.state.userId,
                        reason,
                    });
                    break;
            }

            if (response?.success) {
                let successMessage = "";

                if (this.state.deleteAction === "HARD_DELETE") {
                    successMessage = "User permanently deleted.";
                } else if (this.state.deleteAction === "SOFT_DELETE") {
                    successMessage = "User archived successfully.";
                } else {
                    successMessage = "User restored successfully.";
                }



                this.setState({
                    isDeleteConfirmModalVisible: false,
                    deleteAction: null,
                });


                toastService?.success(

                    successMessage,

                );
                setTimeout(() => {
                    history.push("/users")
                }, 1000);
            } else {
                toastService.error(response?.message || "Operation failed");
            }
        } catch (error) {
            console.error(error);
            toastService.error("Unexpected error occurred.");
        }
    };
    render() {
        return (
            <>
             <DeleteUserDialog
                    visible={this.state.isDeleteConfirmModalVisible}
                    onHide={this.hideDeleteModal}
                    action={this.state.deleteAction}
                    onConfirm={(reason) => {
                        return this.deleteConfirm(reason);
                    }}
                />
            <PageLayout heading="User Profile" icon={UserCog}>
                <div className="space-y-4">
                    <AppCard className="mb-3">
                        <AppCard.Header title="User Details" />
                        <AppCard.Body>{this.renderUserDetails()}</AppCard.Body>
                    </AppCard>
                </div>
            </PageLayout>
            </>
        );
    }
}
